import { forwardRef, type ComponentType, type ElementType, type ReactNode } from 'react';
import {
  StyleSheet,
  Text,
  TextInput,
  View,
  type StyleProp,
  type TextInputProps,
  type ViewStyle,
} from 'react-native';

import { colors, fonts, fontSizes, radii, shadows, spacing } from '@/constants/theme';

type IconProps = { color: string; size: number };

export interface InputProps extends Omit<TextInputProps, 'style'> {
  label?: string;
  /** Lucide icon component. Renders inside the input pill, left-aligned. */
  icon?: ComponentType<IconProps>;
  /** Element rendered after the input (e.g. `<ActivityIndicator/>` for search). */
  trailing?: ReactNode;
  focused?: boolean;
  error?: string;
  size?: 'default' | 'compact';
  style?: StyleProp<ViewStyle>;
  /**
   * Swap the underlying text input, e.g. gorhom's BottomSheetTextInput
   * inside a bottom sheet: the sheet only follows the keyboard for inputs it
   * knows about. Default: React Native's TextInput. Typed loosely because
   * gorhom declares its ref against gesture-handler's TextInput type, not
   * React Native's, though it renders the same component.
   */
  textInputComponent?: ElementType;
}

/**
 * Form input wrapper. Codifies the unified input chrome from onboarding —
 * 1.5px border, 20px radius, identical vertical padding whether or not the
 * input has a leading icon. The audit-caught school-vs-sport mismatch
 * is structurally impossible here (one wrapper, all callers go through it).
 */
export const Input = forwardRef<TextInput, InputProps>(function Input(
  {
    label,
    icon: Icon,
    trailing,
    focused,
    error,
    onFocus,
    onBlur,
    accessibilityLabel,
    size = 'default',
    style,
    textInputComponent: TextInputComponent = TextInput,
    ...rest
  },
  ref,
) {
  // Internal focused state if caller doesn't pass one
  const isFocused = focused ?? false;
  const labelText = label ?? accessibilityLabel;
  const isCompact = size === 'compact';

  return (
    <View style={style}>
      {label && <Text style={styles.label}>{label}</Text>}
      <View
        style={[
          styles.wrap,
          isCompact && styles.wrapCompact,
          isFocused && !error && styles.wrapFocused,
          !!error && styles.wrapError,
        ]}
      >
        {Icon && (
          <Icon
            color={isFocused ? colors.electricGreen : colors.textMuted}
            size={18}
          />
        )}
        <TextInputComponent
          ref={ref}
          style={[styles.input, isCompact && styles.inputCompact]}
          placeholderTextColor={colors.textMuted}
          accessibilityLabel={labelText}
          onFocus={onFocus}
          onBlur={onBlur}
          {...rest}
        />
        {trailing}
      </View>
      {error && <Text style={styles.error}>{error}</Text>}
    </View>
  );
});

const styles = StyleSheet.create({
  label: {
    fontFamily: fonts.bodyMedium,
    fontSize: fontSizes.sm,
    color: colors.text,
    marginBottom: spacing.xs,
  },
  wrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    backgroundColor: colors.surface,
    borderWidth: 1.5,
    borderColor: colors.border,
    borderRadius: radii.lg,
    paddingHorizontal: spacing.base,
    ...shadows.card,
  },
  wrapCompact: {
    borderRadius: radii.base,
    paddingHorizontal: spacing.md,
  },
  wrapFocused: {
    borderColor: colors.electricGreen,
    boxShadow: `0 0 0 3px ${colors.focusRing}`,
  },
  wrapError: {
    borderColor: colors.danger,
  },
  input: {
    flex: 1,
    // Web <input> has an intrinsic width; without this it refuses to shrink
    // when two Inputs share a row (SignupForm name row) and overflows the card.
    minWidth: 0,
    fontFamily: fonts.body,
    fontSize: fontSizes.lg,
    color: colors.text,
    paddingVertical: spacing.base,
    outlineColor: 'transparent',
    outlineStyle: 'solid',
    outlineWidth: 0,
  },
  inputCompact: {
    fontSize: fontSizes.base,
    paddingVertical: spacing.md,
  },
  error: {
    fontFamily: fonts.bodyMedium,
    fontSize: fontSizes.sm,
    color: colors.danger,
    marginTop: spacing.xs,
  },
});
